export type OnboardingJourneySource = 'chat' | 'forward' | 'direct';

export type OnboardingProjectIntentStatus =
    | 'configured'
    | 'verification_requested'
    | 'confirmed'
    | 'adopted'
    | 'expired'
    | 'cancelled';

export type OnboardingPreviewState =
    | 'searching'
    | 'ranking'
    | 'ready'
    | 'empty'
    | 'expired';

export type OnboardingDiscoveryAction =
    | 'adopted_in_flight'
    | 'adopted_ready'
    | 'adopted_empty'
    | 'queued_first_run';

export interface OnboardingProjectIntent {
    intentId: string;
    projectId: string;
    activationGeneration: number;
    status: OnboardingProjectIntentStatus;
    expiresAt: string;
    verificationRequired: boolean;
}

export interface OnboardingPreviewTeaser {
    title: string;
    relevanceLabel: string;
    excerpt: string | null;
}

export interface OnboardingProjectPreview {
    state: OnboardingPreviewState;
    teasers: OnboardingPreviewTeaser[];
}

export interface OnboardingVerificationAccepted {
    status: 'accepted';
    intentId: string;
    verificationGeneration: string;
    finalizationRequestId: string;
    expiresAt: string;
}

export interface OnboardingIntentFinalization {
    status: 'adopted' | 'already_adopted';
    projectId: string;
    intentId: string;
    activationGeneration: number;
    discoveryAction: OnboardingDiscoveryAction;
    destination: string;
}

const INTENT_STATUSES = new Set<OnboardingProjectIntentStatus>([
    'configured',
    'verification_requested',
    'confirmed',
    'adopted',
    'expired',
    'cancelled',
]);
const PREVIEW_STATES = new Set<OnboardingPreviewState>([
    'searching',
    'ranking',
    'ready',
    'empty',
    'expired',
]);
const DISCOVERY_ACTIONS = new Set<OnboardingDiscoveryAction>([
    'adopted_in_flight',
    'adopted_ready',
    'adopted_empty',
    'queued_first_run',
]);
const TEASER_KEYS = new Set(['title', 'relevance_label', 'excerpt']);
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:8001';

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export class OnboardingSubscriptionRequiredError extends Error {
    constructor() {
        super('PROJECT_SUBSCRIPTION_REQUIRED');
        this.name = 'OnboardingSubscriptionRequiredError';
    }
}

export function parseOnboardingFinalizationError(status: number, payload: unknown): Error {
    if (status === 403 && isRecord(payload) && isRecord(payload.detail)
        && payload.detail.code === 'PROJECT_SUBSCRIPTION_REQUIRED') {
        return new OnboardingSubscriptionRequiredError();
    }
    // i18n-ignore: technical-token -- callers render localized error descriptors.
    return new Error(status === 409
        ? 'Project confirmation changed. Reload and try again.'
        : status === 410
            ? 'Project confirmation expired. Create the topic again.'
            : 'We could not open your project. Please try again.');
}

function requiredString(
    value: unknown,
    field: string,
    maxLength = 512
): string {
    if (typeof value !== 'string') {
        throw new Error(`Invalid ${field} in onboarding response.`);
    }
    const normalized = value.trim();
    if (!normalized || normalized.length > maxLength) {
        throw new Error(`Invalid ${field} in onboarding response.`);
    }
    return normalized;
}

function requiredGeneration(value: unknown): number {
    if (!Number.isSafeInteger(value) || Number(value) < 1) {
        throw new Error('Invalid activation_generation in onboarding response.');
    }
    return Number(value);
}

function parseExpiry(value: unknown): string {
    const expiresAt = requiredString(value, 'expires_at', 64);
    if (!Number.isFinite(Date.parse(expiresAt))) {
        throw new Error('Invalid expires_at in onboarding response.');
    }
    return expiresAt;
}

export function parseOnboardingProjectIntent(value: unknown): OnboardingProjectIntent {
    if (!isRecord(value)) {
        throw new Error('Invalid onboarding intent response.');
    }
    const status = value.status;
    if (typeof status !== 'string' || !INTENT_STATUSES.has(status as OnboardingProjectIntentStatus)) {
        throw new Error('Invalid onboarding intent status.');
    }
    if (typeof value.verification_required !== 'boolean') {
        throw new Error('Invalid verification_required in onboarding response.');
    }
    return {
        intentId: requiredString(value.intent_id, 'intent_id', 64),
        projectId: requiredString(value.project_id, 'project_id', 64),
        activationGeneration: requiredGeneration(value.activation_generation),
        status: status as OnboardingProjectIntentStatus,
        expiresAt: parseExpiry(value.expires_at),
        verificationRequired: value.verification_required,
    };
}

function parseTeaser(value: unknown): OnboardingPreviewTeaser {
    if (!isRecord(value) || Object.keys(value).some((key) => !TEASER_KEYS.has(key))) {
        throw new Error('Preview teaser contained unsupported fields.');
    }
    return {
        title: requiredString(value.title, 'teaser title', 300),
        relevanceLabel: requiredString(value.relevance_label, 'relevance_label', 80),
        excerpt: value.excerpt === null
            ? null
            : requiredString(value.excerpt, 'teaser excerpt', 600),
    };
}

export function parseOnboardingProjectPreview(value: unknown): OnboardingProjectPreview {
    if (!isRecord(value)) {
        throw new Error('Invalid onboarding preview response.');
    }
    const state = value.state;
    if (typeof state !== 'string' || !PREVIEW_STATES.has(state as OnboardingPreviewState)) {
        throw new Error('Invalid onboarding preview state.');
    }
    if (!Array.isArray(value.teasers) || value.teasers.length > 3) {
        throw new Error('Invalid onboarding preview teasers.');
    }
    const teasers = value.teasers.map(parseTeaser);
    if ((state === 'ready') !== (teasers.length > 0)) {
        throw new Error('Onboarding preview state did not match its teaser payload.');
    }
    return { state: state as OnboardingPreviewState, teasers };
}

export function parseOnboardingVerificationAccepted(
    value: unknown,
    expectedIntentId: string,
    expectedVerificationGeneration: string
): OnboardingVerificationAccepted {
    if (!isRecord(value) || value.status !== 'accepted') {
        throw new Error('Invalid verification request response.');
    }
    const response = {
        status: 'accepted' as const,
        intentId: requiredString(value.intent_id, 'intent_id', 64),
        verificationGeneration: requiredString(
            value.verification_generation,
            'verification_generation',
            64
        ),
        finalizationRequestId: requiredString(
            value.finalization_request_id,
            'finalization_request_id',
            64
        ),
        expiresAt: parseExpiry(value.expires_at),
    };
    if (
        response.intentId !== expectedIntentId
        || response.verificationGeneration !== expectedVerificationGeneration
        || response.finalizationRequestId !== expectedVerificationGeneration
    ) {
        throw new Error('Verification request response binding mismatch.');
    }
    return response;
}

export function parseOnboardingIntentFinalization(
    value: unknown,
    expectedIntent: Pick<OnboardingProjectIntent, 'intentId' | 'projectId' | 'activationGeneration'>
): OnboardingIntentFinalization {
    if (!isRecord(value) || (value.status !== 'adopted' && value.status !== 'already_adopted')) {
        throw new Error('Invalid onboarding finalization response.');
    }
    const discoveryAction = requiredString(value.discovery_action, 'discovery_action', 80);
    if (!DISCOVERY_ACTIONS.has(discoveryAction as OnboardingDiscoveryAction)) {
        throw new Error('Invalid discovery_action in onboarding response.');
    }
    const result = {
        status: value.status,
        projectId: requiredString(value.project_id, 'project_id', 64),
        intentId: requiredString(value.intent_id, 'intent_id', 64),
        activationGeneration: requiredGeneration(value.activation_generation),
        discoveryAction: discoveryAction as OnboardingDiscoveryAction,
        destination: requiredString(value.destination, 'destination', 1024),
    } as OnboardingIntentFinalization;
    if (
        result.intentId !== expectedIntent.intentId
        || result.projectId !== expectedIntent.projectId
        || result.activationGeneration !== expectedIntent.activationGeneration
    ) {
        throw new Error('Onboarding finalization response binding mismatch.');
    }
    const destination = new URL(result.destination, 'https://paperzilla.invalid');
    const expectedPath = `/dashboard/${encodeURIComponent(expectedIntent.projectId)}/settings`;
    if (
        !result.destination.startsWith('/')
        || result.destination.startsWith('//')
        || destination.origin !== 'https://paperzilla.invalid'
        || destination.pathname !== expectedPath
        || destination.searchParams.get('onboarding') !== 'preparing'
    ) {
        throw new Error('Onboarding finalization returned an unsafe destination.');
    }
    return result;
}

export function onboardingVerificationEndpointCandidates({
    isWeb,
    hostname,
    intentId,
}: {
    isWeb: boolean;
    hostname?: string;
    intentId: string;
}): string[] {
    const path = `/api/onboarding/project-intents/${encodeURIComponent(intentId)}/verification-request`;
    if (!isWeb) {
        const configuredBase = (process.env.EXPO_PUBLIC_BASE_URL || 'https://paperzilla.ai')
            .replace(/\/+$/, '');
        return [`${configuredBase}${path}`];
    }
    if (hostname && LOCAL_HOSTS.has(hostname)) {
        return [`${DEFAULT_LOCAL_API_BASE_URL}${path}`];
    }
    return [path];
}

export function onboardingFinalizationEndpointCandidates({
    isWeb,
    hostname,
    intentId,
}: {
    isWeb: boolean;
    hostname?: string;
    intentId: string;
}): string[] {
    const path = `/api/onboarding/project-intents/${encodeURIComponent(intentId)}/finalize`;
    if (!isWeb) {
        const configuredBase = (process.env.EXPO_PUBLIC_BASE_URL || 'https://paperzilla.ai')
            .replace(/\/+$/, '');
        return [`${configuredBase}${path}`];
    }
    if (hostname && LOCAL_HOSTS.has(hostname)) {
        return [`${DEFAULT_LOCAL_API_BASE_URL}${path}`];
    }
    return [path];
}

export function buildOnboardingActivationPath(projectId: string): string {
    return `/dashboard/${encodeURIComponent(projectId)}/activate`;
}
